import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db, auth } from "../../firebase/config";
import { BrandIcon } from "../../components/BrandIcon";
import { uploadCmsImage, createCmsDoc } from "../cmsApi.jsx";
import {
  BRAND_ICON_OPTIONS,
  BRAND_ICON_CATEGORIES,
  CMS_ICON_SPECS,
  CMS_ICON_CLASSIFICATIONS,
  CMS_ICON_CLASSIFICATION_HINTS,
  DEFAULT_CMS_ICON_CLASSIFICATION,
  findBrandIconOption,
  isIconUrl,
  normalizeCmsIconClassification,
  resolveBrandIconId,
  validateCmsIconFile,
} from "../../lib/cmsIconCatalog";
import {
  LUCIDE_ICON_OPTIONS,
  REACT_ICON_OPTIONS,
  findLibraryIconOption,
} from "../../lib/libraryIcons";
import { SOCIAL_PRESET_OPTIONS } from "../../lib/socialPlatforms";

/**
 * Selector: marca AGDYSA + Lucide + react-icons + uploads clasificados + presets redes.
 * Guarda: brandId | lucide:Name | ri:Name | URL
 *
 * @param {string} [preferredClassification] - p.ej. "redes" | "navegacion": prioriza pestaña y filtra subidos
 */
export default function IconPicker({
  value,
  onChange,
  label = "Icono",
  preferredClassification,
}) {
  const preferClass = preferredClassification
    ? normalizeCmsIconClassification(preferredClassification)
    : null;
  const preferRedes = preferClass === "redes";
  const preferNav = preferClass === "navegacion";
  const initialTab = preferRedes ? "redes" : preferNav ? "lucide" : "brand";
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(initialTab);
  const [brandFilter, setBrandFilter] = useState("all");
  const [customFilter, setCustomFilter] = useState(preferClass || "all");
  const [uploadClassification, setUploadClassification] = useState(
    preferClass || DEFAULT_CMS_ICON_CLASSIFICATION
  );
  const [search, setSearch] = useState("");

  const groupedBrand = useMemo(() => {
    const map = {};
    const q = search.trim().toLowerCase();
    for (const item of BRAND_ICON_OPTIONS) {
      if (brandFilter !== "all" && item.category !== brandFilter) continue;
      if (q && !item.label.toLowerCase().includes(q) && !item.key.toLowerCase().includes(q)) continue;
      if (!map[item.category]) map[item.category] = [];
      map[item.category].push(item);
    }
    return map;
  }, [brandFilter, search]);

  const lucideFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return LUCIDE_ICON_OPTIONS;
    return LUCIDE_ICON_OPTIONS.filter(
      (i) => i.label.toLowerCase().includes(q) || i.name.toLowerCase().includes(q)
    );
  }, [search]);

  const riFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return REACT_ICON_OPTIONS;
    return REACT_ICON_OPTIONS.filter(
      (i) => i.label.toLowerCase().includes(q) || i.name.toLowerCase().includes(q)
    );
  }, [search]);

  const redesPresets = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return SOCIAL_PRESET_OPTIONS;
    return SOCIAL_PRESET_OPTIONS.filter(
      (i) => i.label.toLowerCase().includes(q) || i.key.toLowerCase().includes(q)
    );
  }, [search]);

  const customFiltered = useMemo(() => {
    return custom.filter((item) => {
      const cls = normalizeCmsIconClassification(item.classification || item.category);
      if (customFilter !== "all" && cls !== customFilter) return false;
      return true;
    });
  }, [custom, customFilter]);

  const loadCustom = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "cmsIcons"), orderBy("name", "asc"));
      const snap = await getDocs(q);
      setCustom(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch {
      try {
        const snap = await getDocs(collection(db, "cmsIcons"));
        setCustom(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch {
        setCustom([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadCustom();
  }, [open]);

  const selectedBrand = findBrandIconOption(value);
  const selectedLib = findLibraryIconOption(value);
  const selectedPreset = SOCIAL_PRESET_OPTIONS.find((p) => p.key === value);
  const selectedCustom = isIconUrl(value)
    ? custom.find((c) => c.url === value) || { name: "Personalizado", url: value }
    : null;

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = validateCmsIconFile(file);
    if (err) {
      setError(err);
      return;
    }
    setUploading(true);
    setError("");
    try {
      const url = await uploadCmsImage("icons", file);
      const name =
        file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim() || "Icono";
      const classification = normalizeCmsIconClassification(uploadClassification);
      await createCmsDoc("cmsIcons", {
        name,
        url,
        classification,
        updatedBy: auth.currentUser?.uid || null,
      });
      await loadCustom();
      onChange(url);
      setOpen(false);
    } catch (err2) {
      setError(
        err2?.message?.includes("storage") || err2?.code?.includes("storage")
          ? "No se pudo subir. Active Firebase Storage y despliegue storage.rules."
          : err2?.message || "Error al subir icono."
      );
    } finally {
      setUploading(false);
    }
  };

  const renderPreview = () => {
    if (isIconUrl(value)) {
      return <img src={value} alt="" className="w-8 h-8 object-contain" />;
    }
    if (selectedPreset?.Component) {
      const Comp = selectedPreset.Component;
      return <Comp size={28} className="text-brand-icon" />;
    }
    if (selectedLib?.Component) {
      const Comp = selectedLib.Component;
      return <Comp size={28} className="text-brand-icon" />;
    }
    if (selectedBrand) {
      return <BrandIcon id={selectedBrand.brandId} size={28} />;
    }
    if (resolveBrandIconId(value)) {
      return <BrandIcon id={resolveBrandIconId(value)} size={28} />;
    }
    return <span className="text-xs text-brand-dark/40">—</span>;
  };

  const selectedLabel =
    selectedPreset?.label ||
    selectedBrand?.label ||
    selectedLib?.label ||
    selectedCustom?.name ||
    value ||
    "Sin icono";

  const preferUploadsCount = preferClass
    ? custom.filter(
        (c) => normalizeCmsIconClassification(c.classification || c.category) === preferClass
      ).length
    : 0;

  const tabs = [
    preferRedes ? { id: "redes", label: `Redes (${SOCIAL_PRESET_OPTIONS.length})` } : null,
    preferNav
      ? {
          id: "custom",
          label: `Menú / subidos (${preferUploadsCount})`,
        }
      : null,
    { id: "brand", label: `Marca (${BRAND_ICON_OPTIONS.length})` },
    { id: "lucide", label: `Lucide (${LUCIDE_ICON_OPTIONS.length})` },
    { id: "ri", label: `React Icons (${REACT_ICON_OPTIONS.length})` },
    !preferNav ? { id: "custom", label: `Subidos (${custom.length})` } : null,
    !preferRedes ? { id: "redes", label: `Redes (${SOCIAL_PRESET_OPTIONS.length})` } : null,
  ].filter(Boolean);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-widest text-brand-dark/55">
          {label}
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[11px] font-bold uppercase tracking-widest text-brand-primary"
        >
          {open ? "Cerrar" : "Elegir"}
        </button>
      </div>

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-3 rounded-2xl border border-brand-soft bg-white px-4 py-3 text-left hover:border-brand-primary/40"
      >
        <span className="w-10 h-10 rounded-xl bg-brand-soft/60 flex items-center justify-center shrink-0 overflow-hidden">
          {renderPreview()}
        </span>
        <span className="text-sm font-medium text-brand-dark truncate">{selectedLabel}</span>
      </button>

      {open && (
        <div className="rounded-2xl border border-brand-soft bg-[#f8faf9] p-4 space-y-4 max-h-[480px] overflow-y-auto">
          <div className="flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                  tab === t.id ? "bg-brand-primary text-white" : "bg-white border border-brand-soft"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab !== "custom" && (
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar icono…"
              className="w-full rounded-xl border border-brand-soft bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-primary"
            />
          )}

          {tab === "redes" && (
            <div className="space-y-3">
              <p className="text-[11px] text-brand-dark/55">
                Presets de redes. Para logos propios: pestaña Subidos (clasificación Redes sociales) o
                admin Iconos.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {redesPresets.map((item) => {
                  const Comp = item.Component;
                  const active = value === item.key;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        onChange(item.key);
                        setOpen(false);
                      }}
                      className={`group flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-transform ${
                        active
                          ? "border-brand-primary bg-brand-primary/10"
                          : "border-brand-soft bg-white hover:border-brand-primary/30"
                      }`}
                    >
                      <Comp size={22} className="text-brand-icon shrink-0 transition-transform duration-200 group-hover:scale-125 group-focus-visible:scale-125" />
                      <span className="text-xs font-bold text-brand-dark leading-tight line-clamp-2 transition-transform duration-200 origin-left group-hover:scale-110 group-focus-visible:scale-110">
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              {custom.filter(
                (c) => normalizeCmsIconClassification(c.classification || c.category) === "redes"
              ).length > 0 && (
                <>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40">
                    Subidos · Redes
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {custom
                      .filter(
                        (c) =>
                          normalizeCmsIconClassification(c.classification || c.category) === "redes"
                      )
                      .map((item) => {
                        const active = value === item.url;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => {
                              onChange(item.url);
                              setOpen(false);
                            }}
                            className={`group flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-transform ${
                              active
                                ? "border-brand-primary bg-brand-primary/10"
                                : "border-brand-soft bg-white hover:border-brand-primary/30"
                            }`}
                          >
                            <img
                              src={item.url}
                              alt=""
                              className="w-6 h-6 object-contain shrink-0 transition-transform duration-200 group-hover:scale-125 group-focus-visible:scale-125"
                            />
                            <span className="text-xs font-bold text-brand-dark leading-tight line-clamp-2 transition-transform duration-200 origin-left group-hover:scale-110 group-focus-visible:scale-110">
                              {item.name}
                            </span>
                          </button>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "brand" && (
            <>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setBrandFilter("all")}
                  className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                    brandFilter === "all" ? "bg-brand-dark text-white" : "bg-white border border-brand-soft"
                  }`}
                >
                  Todas
                </button>
                {Object.entries(BRAND_ICON_CATEGORIES).map(([key, title]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setBrandFilter(key)}
                    className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                      brandFilter === key ? "bg-brand-dark text-white" : "bg-white border border-brand-soft"
                    }`}
                  >
                    {title}
                  </button>
                ))}
              </div>
              {Object.entries(groupedBrand).map(([category, items]) => (
                <div key={category}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40 mb-2">
                    {BRAND_ICON_CATEGORIES[category] || category}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {items.map((item) => {
                      const active = resolveBrandIconId(value) === item.key;
                      return (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => {
                            onChange(item.key);
                            setOpen(false);
                          }}
                          className={`group flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-transform ${
                            active
                              ? "border-brand-primary bg-brand-primary/10"
                              : "border-brand-soft bg-white hover:border-brand-primary/30"
                          }`}
                        >
                          <span className="inline-flex transition-transform duration-200 group-hover:scale-125 group-focus-visible:scale-125">
                            <BrandIcon id={item.brandId} size={24} />
                          </span>                          <span className="text-xs font-bold text-brand-dark leading-tight line-clamp-2 transition-transform duration-200 origin-left group-hover:scale-110 group-focus-visible:scale-110">
                            {item.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}

          {tab === "lucide" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {lucideFiltered.map((item) => {
                const Comp = item.Component;
                const active = value === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      onChange(item.key);
                      setOpen(false);
                    }}
                    className={`group flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-transform ${
                      active
                        ? "border-brand-primary bg-brand-primary/10"
                        : "border-brand-soft bg-white hover:border-brand-primary/30"
                    }`}
                  >
                    <Comp size={22} className="text-brand-icon shrink-0 transition-transform duration-200 group-hover:scale-125 group-focus-visible:scale-125" />
                    <span className="text-xs font-bold text-brand-dark leading-tight line-clamp-2 transition-transform duration-200 origin-left group-hover:scale-110 group-focus-visible:scale-110">
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {tab === "ri" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {riFiltered.map((item) => {
                const Comp = item.Component;
                const active = value === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      onChange(item.key);
                      setOpen(false);
                    }}
                    className={`group flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-transform ${
                      active
                        ? "border-brand-primary bg-brand-primary/10"
                        : "border-brand-soft bg-white hover:border-brand-primary/30"
                    }`}
                  >
                    <Comp size={22} className="text-brand-icon shrink-0 transition-transform duration-200 group-hover:scale-125 group-focus-visible:scale-125" />
                    <span className="text-xs font-bold text-brand-dark leading-tight line-clamp-2 transition-transform duration-200 origin-left group-hover:scale-110 group-focus-visible:scale-110">
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {tab === "custom" && (
            <div className="space-y-3">
              <p className="text-[11px] text-brand-dark/55">
                Archivos del cliente (no Marca ni Lucide).{" "}
                {CMS_ICON_SPECS.formatsLabel}. {CMS_ICON_SPECS.sizeLabel}. {CMS_ICON_SPECS.weightLabel}.
              </p>
              {preferClass && CMS_ICON_CLASSIFICATION_HINTS[preferClass] && (
                <p className="text-[11px] text-brand-primary/90 font-medium">
                  Contexto: {CMS_ICON_CLASSIFICATIONS[preferClass]} —{" "}
                  {CMS_ICON_CLASSIFICATION_HINTS[preferClass]}
                </p>
              )}
              <label className="block space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/50">
                  Etiqueta al subir (filtro en Subidos)
                </span>
                <select
                  className="w-full rounded-xl border border-brand-soft bg-white px-3 py-2 text-sm"
                  value={uploadClassification}
                  onChange={(e) => setUploadClassification(e.target.value)}
                >
                  {Object.entries(CMS_ICON_CLASSIFICATIONS).map(([key, title]) => (
                    <option key={key} value={key}>
                      {title}
                    </option>
                  ))}
                </select>
                <span className="block text-[10px] text-brand-dark/45 mt-1">
                  {CMS_ICON_CLASSIFICATION_HINTS[uploadClassification] || ""}
                </span>
              </label>
              <label className="inline-flex text-[11px] font-bold uppercase tracking-widest text-brand-primary cursor-pointer">
                {uploading ? "Subiendo…" : "+ Subir icono"}
                <input
                  type="file"
                  accept={CMS_ICON_SPECS.accept}
                  className="hidden"
                  disabled={uploading}
                  onChange={onUpload}
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setCustomFilter("all")}
                  className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                    customFilter === "all" ? "bg-brand-dark text-white" : "bg-white border border-brand-soft"
                  }`}
                >
                  Todas
                </button>
                {Object.entries(CMS_ICON_CLASSIFICATIONS).map(([key, title]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setCustomFilter(key)}
                    className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                      customFilter === key ? "bg-brand-dark text-white" : "bg-white border border-brand-soft"
                    }`}
                  >
                    {title}
                  </button>
                ))}
              </div>
              {loading && <p className="text-xs text-brand-dark/40">Cargando…</p>}
              {!loading && customFiltered.length === 0 && (
                <p className="text-xs text-brand-dark/45">Aún no hay iconos en esta clasificación.</p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {customFiltered.map((item) => {
                  const active = value === item.url;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        onChange(item.url);
                        setOpen(false);
                      }}
                      className={`group flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-transform ${
                        active
                          ? "border-brand-primary bg-brand-primary/10"
                          : "border-brand-soft bg-white hover:border-brand-primary/30"
                      }`}
                    >
                      <img
                        src={item.url}
                        alt=""
                        className="w-6 h-6 object-contain shrink-0 transition-transform duration-200 group-hover:scale-125 group-focus-visible:scale-125"
                      />
                      <span className="text-xs font-bold text-brand-dark leading-tight line-clamp-2 transition-transform duration-200 origin-left group-hover:scale-110 group-focus-visible:scale-110">
                        {item.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}

